import {
  BufferGeometry,
  InstancedMesh,
  Material,
  Matrix4,
  Quaternion,
  Sphere,
  Vector3,
} from 'three'

export interface GeoInstance {
  wx: number
  wz: number
  scale: number
  rotation: number
}

export function chunkInstances(
  instances: GeoInstance[],
  side: number,
  centerOffset: number,
  chunksX: number,
  chunksZ: number,
): GeoInstance[][] {
  const chunkW = side / chunksX
  const chunkD = side / chunksZ
  const buckets: GeoInstance[][] = Array.from({ length: chunksX * chunksZ }, () => [])
  for (const inst of instances) {
    const wx = inst.wx - centerOffset
    const xi = Math.max(0, Math.min(chunksX - 1, Math.floor((wx + side / 2) / chunkW)))
    const zi = Math.max(0, Math.min(chunksZ - 1, Math.floor((inst.wz + side / 2) / chunkD)))
    buckets[zi * chunksX + xi].push(inst)
  }
  return buckets
}

const _pos = new Vector3()
const _quat = new Quaternion()
const _scale = new Vector3()
const _mat = new Matrix4()
const _axis = new Vector3(0, 1, 0)

export function buildInstancedFromGeo(
  geo: BufferGeometry,
  mat: Material,
  instances: GeoInstance[],
  xzOffset?: Vector3,
  castShadow = false,
  boundingSphere?: Sphere,
): InstancedMesh {
  const cloned = geo.clone()
  if (xzOffset) {
    cloned.translate(-xzOffset.x, 0, -xzOffset.z)
  } else {
    cloned.computeBoundingBox()
    const center = cloned.boundingBox!.getCenter(new Vector3())
    cloned.translate(-center.x, 0, -center.z)
  }

  const mesh = new InstancedMesh(cloned, mat, instances.length)
  mesh.castShadow = castShadow
  mesh.receiveShadow = true

  for (let i = 0; i < instances.length; i++) {
    const { wx, wz, scale, rotation } = instances[i]
    _pos.set(wx, 0, wz)
    _quat.setFromAxisAngle(_axis, rotation)
    _scale.setScalar(scale)
    _mat.compose(_pos, _quat, _scale)
    mesh.setMatrixAt(i, _mat)
  }

  mesh.instanceMatrix.needsUpdate = true
  cloned.computeBoundingSphere()
  if (boundingSphere) {
    mesh.boundingSphere = boundingSphere
  }
  return mesh
}

export interface ChunkConfig {
  side: number
  centerOffset: number
  chunksX: number
  chunksZ: number
}

export function buildChunkedMeshes(
  geo: BufferGeometry,
  mat: Material,
  instances: GeoInstance[],
  chunkCfg: ChunkConfig,
  xzOffset?: Vector3,
  castShadow = false,
): InstancedMesh[] {
  const { side, centerOffset, chunksX, chunksZ } = chunkCfg
  const chunkW = side / chunksX
  const chunkD = side / chunksZ
  const chunks = chunkInstances(instances, side, centerOffset, chunksX, chunksZ)
  const result: InstancedMesh[] = []

  for (let bi = 0; bi < chunks.length; bi++) {
    const insts = chunks[bi]
    if (!insts.length) continue
    const xi = bi % chunksX
    const zi = Math.floor(bi / chunksX)
    const cx = xi * chunkW - side / 2 + centerOffset
    const cz = zi * chunkD - side / 2
    const sphere = new Sphere(
      new Vector3(cx + chunkW / 2, 0, cz + chunkD / 2),
      Math.sqrt((chunkW / 2) ** 2 + (chunkD / 2) ** 2),
    )
    result.push(buildInstancedFromGeo(geo, mat, insts, xzOffset, castShadow, sphere))
  }

  return result
}
